import { TargetTypeEnum } from '~/entities/EIB/MyRequests/types';

export type Category = {
  name: string;
  code: string;
};

export type CompensationPlan = {
  planType: string;
  category: Category;
  name: string;
  code: string;
  allowedTargetType?: TargetTypeEnum;
  active: boolean;
};

export type DefaultCompensationPlan = CompensationPlan & {
  workdayId: string;
  currency: string;
  frequency: string;
  compensationPlanType: string;
  calculationType: string;
  targetEligibility: number;
  active: boolean;
};

export type Reason = {
  code: string;
  workdayReasonId: string;
  name: string;
};

export type NewRequestFormProps = {
  requestCompensationType: RequestCompensationType;
  onSubmit: (data: RequestCompensationData) => void | Promise<void>;
};

export type ReasonSelectorProps = {
  searchTerm: string;
  filteredReasons: Reason[];
  isListOpen: boolean;
  disabled?: boolean;
  width?: string;
  containerRef: React.RefObject<HTMLDivElement>;
  handleReasonFocus: () => void;
  onSearch: (value: string) => void;
  onSelect: (reason: Reason) => void;
};

export type DateSelectionSectionProps = {
  effectiveDateWithRules: Date;
  visibilityDate: Date;
  onEffectiveDateChange: (date: Date) => void;
  onVisibilityDateChange: (date: Date) => void;
};

export type PlanSelectorProps = {
  searchTerm: string;
  filteredPlans: CompensationPlan[];
  selectedCount: number;
  isListOpen: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  disabled?: boolean;
  handlePlanFocus: () => void;
  onSearch: (value: string) => void;
  onToggle: (plan: CompensationPlan) => void;
  isPlanSelected: (planCode: string) => boolean;
};

export type TargetType = 'percent' | 'amount' | 'change_salary_value';

export type SalaryIncreaseButtonsProps = {
  requestCompensationType: RequestCompensationType;
  targetType: TargetType;
  onSelectPercent: () => void;
  onSelectIncrease: () => void;
  onSelectChangeInteger: () => void;
};

export type CompensationChangesType = {
  planId?: string;
  planCategoryCode?: string;
  rows: ProcessedCompensationRow[];
};

export type RequestCompensationData = {
  eibType: EIBType;
  requestCompensationType: RequestCompensationType;
  effectiveDateWithRules: Date;
  visibilityDate: Date;
  reason: string;
  workdayReasonId: string;
  zone: string;
  compensationChanges: CompensationChangesType[];
};

export enum RequestCompensationType {
  SALARY_INCREASE = 'SALARY_INCREASE',
  ASSIGN_PLAN = 'ASSIGN_PLAN',
  REMOVE_PLAN = 'REMOVE_PLAN',
  DEFAULT_COMPENSATION_CHANGE = 'DEFAULT_COMPENSATION_CHANGE'
}

export type CompensationRow = {
  id: string;
  employeeId: string;
  position: string;
  package?: string;
  grade?: string;
  gradeProfile?: string;
  step?: string;
  targetType?: TargetType;
};

export type TargetAmountRow = {
  id: string;
  employeeId: string;
  position: string;
  target: string;
  package?: string;
  grade?: string;
  gradeProfile?: string;
  step?: string;
  targetType?: TargetType;
};

export type ProcessedCompensationRow = {
  employeeId: string;
  position: string;
  target?: string;
  package?: string;
  grade?: string;
  gradeProfile?: string;
  step?: string;
  targetType: TargetType;
};

export enum EIBType {
  REQUEST_COMPENSATION = 'REQUEST_COMPENSATION',
  STATUS_POSITION = 'STATUS_POSITION'
}

export type RequestGridProps = {
  name: string;
  requestCompensationType: RequestCompensationType;
  isTargetGrid?: boolean;
  canRemove?: boolean;
  calculationType?: TargetTypeEnum;
  zone?: string;
  errors?: Record<string, string>;
  disabled?: boolean;
  onRemoveGrid: () => void;
  onRowsChange?: (
    rows: CompensationRow[] | TargetAmountRow[],
    targetType?: TargetType
  ) => void;
};

export type StatusPositionSectionData = {
  action: 'freeze' | 'unfreeze' | 'close';
  reason: string;
  positions: string[];
};

export type StatusPositionData = {
  eibType: EIBType;
  zone: string;
  effectiveDate: Date | undefined;
  statusPositionChanges: StatusPositionSectionData[];
};

export enum CompensationPackage {
  STANDARD = 'STANDARD',
  PREMIUM = 'PREMIUM'
}

export type DefaultCompensationRow = {
  id: string;
  positionId: string;
  gradeProfile: string;
  target: string;
};

export type DefaultCompensationTableData = {
  id: string;
  action: 'assign' | 'remove';
  planCode: string;
  rows: DefaultCompensationRow[];
};

export type DefaultCompensationChangeData = {
  eibType: EIBType;
  zone: string;
  effectiveDate: string;
  package: CompensationPackage;
  tables: DefaultCompensationTableData[];
};
